import { KIOSK_PIN_HASH, type KioskPinCheck } from '@wr/shared';
import { kioskPin as rules } from '../../../../config/portal.json';
import { ApiError, get, post } from '../api.js';
import type { LocalStore } from './store.js';

/**
 * The kiosk PIN on this device (brief sections 6.4 and 7.5): its hash is
 * kept in the person's encrypted store, so the PIN works at a show without
 * network. After too many wrong PINs, the device refuses any for a while.
 */
const CHECK = 'kioskPin';
const FAILS = 'kioskPinFails';

interface Fails {
  count: number;
  lockedUntil: number;
}

/** Fetches the PIN's hash when online; keeps the last one otherwise. */
export async function refreshKioskPin(store: LocalStore): Promise<void> {
  try {
    await store.setMeta(CHECK, await get<KioskPinCheck>('/capture/kiosk-pin'));
  } catch (error) {
    // No PIN set any more: forget the old one. Offline or refused: keep it.
    if (error instanceof ApiError && error.status === 404) await store.setMeta(CHECK, null);
  }
}

export async function keptKioskPin(store: LocalStore): Promise<KioskPinCheck | null> {
  return store.getMeta<KioskPinCheck>(CHECK);
}

const fromBase64 = (text: string) => Uint8Array.from(atob(text), (c) => c.charCodeAt(0));
const toBase64 = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes));

async function derive(secret: string, salt: Uint8Array, iterations: number): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(secret),
    'PBKDF2',
    false,
    ['deriveBits'],
  );
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: KIOSK_PIN_HASH.hash, salt: salt as BufferSource, iterations },
    key,
    KIOSK_PIN_HASH.bits,
  );
  return new Uint8Array(bits);
}

async function matches(
  secret: string,
  check: { salt: string; hash: string; iterations: number },
): Promise<boolean> {
  const a = await derive(secret, fromBase64(check.salt), check.iterations);
  const b = fromBase64(check.hash);
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

export type PinResult = 'ok' | 'wrong' | 'locked' | 'not_set';

export async function checkKioskPin(
  store: LocalStore,
  pin: string,
  now = Date.now(),
): Promise<{ result: PinResult; lockedUntil?: number }> {
  const check = await keptKioskPin(store);
  if (!check) return { result: 'not_set' };
  const fails = (await store.getMeta<Fails>(FAILS)) ?? { count: 0, lockedUntil: 0 };
  if (fails.lockedUntil > now) return { result: 'locked', lockedUntil: fails.lockedUntil };
  if (await matches(pin, check)) {
    await store.setMeta(FAILS, { count: 0, lockedUntil: 0 });
    return { result: 'ok' };
  }
  const count = fails.count + 1;
  const lockedUntil = count >= rules.maxFailedAttempts ? now + rules.lockoutMinutes * 60_000 : 0;
  await store.setMeta(FAILS, { count: lockedUntil ? 0 : count, lockedUntil });
  return lockedUntil ? { result: 'locked', lockedUntil } : { result: 'wrong' };
}

/**
 * Round 2: never stuck in Hand the tablet or the kiosk. Without a PIN on
 * the device, the seller signed in leaves with their password: checked by
 * the portal with network, else against a salted hash of it kept in their
 * encrypted store since they signed in (never the password itself). Each
 * exit this way is written in the audit log, at once or at the next sync.
 */
const PASSWORD = 'passwordCheck';
const TO_REPORT = 'kioskExitsToReport';

interface SecretCheck {
  salt: string;
  hash: string;
  iterations: number;
}

let pendingPassword: { email: string; check: Promise<SecretCheck> } | null = null;

async function secretCheck(secret: string): Promise<SecretCheck> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iterations = KIOSK_PIN_HASH.iterations;
  return {
    salt: toBase64(salt),
    hash: toBase64(await derive(secret, salt, iterations)),
    iterations,
  };
}

/** At sign-in, the password typed: its hash waits for the person's store to open. */
export function rememberPasswordCheck(email: string, password: string): void {
  const check = secretCheck(password);
  check.catch(() => undefined);
  pendingPassword = { email: email.trim().toLowerCase(), check };
}

/** Keeps the hash remembered at sign-in in this person's store (and forgets it in memory). */
export async function keepPasswordCheck(store: LocalStore, email: string): Promise<void> {
  const pending = pendingPassword;
  if (!pending || pending.email !== email.trim().toLowerCase()) return;
  pendingPassword = null;
  await store.setMeta(PASSWORD, await pending.check);
}

export type ExitMode = 'handoff' | 'kiosk';

/** Checks the password of the person signed in, without network, with the PIN's lockout. */
export async function checkPasswordOffline(
  store: LocalStore,
  password: string,
  now = Date.now(),
): Promise<{ result: PinResult | 'no_check'; lockedUntil?: number }> {
  const check = await store.getMeta<SecretCheck>(PASSWORD);
  if (!check) return { result: 'no_check' };
  const fails = (await store.getMeta<Fails>(FAILS)) ?? { count: 0, lockedUntil: 0 };
  if (fails.lockedUntil > now) return { result: 'locked', lockedUntil: fails.lockedUntil };
  if (await matches(password, check)) {
    await store.setMeta(FAILS, { count: 0, lockedUntil: 0 });
    return { result: 'ok' };
  }
  const count = fails.count + 1;
  const lockedUntil = count >= rules.maxFailedAttempts ? now + rules.lockoutMinutes * 60_000 : 0;
  await store.setMeta(FAILS, { count: lockedUntil ? 0 : count, lockedUntil });
  return lockedUntil ? { result: 'locked', lockedUntil } : { result: 'wrong' };
}

/** After an exit checked on the device: its audit line waits for the network. */
export async function queueKioskExit(store: LocalStore, mode: ExitMode, at = new Date()) {
  const list = (await store.getMeta<{ mode: ExitMode; at: string }[]>(TO_REPORT)) ?? [];
  await store.setMeta(TO_REPORT, [...list, { mode, at: at.toISOString() }]);
}

/** Sends the exits checked without network to the audit log (at each sync). */
export async function reportKioskExits(store: LocalStore): Promise<void> {
  const list = (await store.getMeta<{ mode: ExitMode; at: string }[]>(TO_REPORT)) ?? [];
  for (const [i, exit] of list.entries()) {
    try {
      await post('/capture/kiosk-exit', { mode: exit.mode, offlineAt: exit.at });
    } catch {
      await store.setMeta(TO_REPORT, list.slice(i));
      return;
    }
  }
  if (list.length) await store.setMeta(TO_REPORT, []);
}

/** The password checked by the portal (with network); the device keeps its new hash. */
export async function exitWithPassword(store: LocalStore, password: string, mode: ExitMode) {
  await post('/capture/kiosk-exit', { mode, password });
  await store.setMeta(PASSWORD, await secretCheck(password));
}
